import React, { useEffect, useState } from "react";
import styled from "styled-components";

/**
 * MyPlaylist Component with Drag and Drop Functionality
 * 
 * Features:
 * - Display playlists in a table format
 * - Expand/collapse playlists to show videos
 * - Drag and drop videos between playlists
 * - Reorder videos within the same playlist
 * - Visual feedback during drag operations
 * - API integration for moving and reordering videos
 * 
 * Drag and Drop Usage:
 * 1. Expand a playlist to see its videos
 * 2. Click and drag any video item
 * 3. Drop it on another playlist to move the video
 * 4. Drop it on another video in the same playlist to reorder
 * 5. Visual indicators show valid drop targets and positions
 * 6. Success/error notifications provide feedback
 */

// Style
import {
  TableWrap,
  TableContent,
  TableNames,
  AlfabetSort,
  DateSort,
  LeftContent,
  MidContent,
  RightContent,
  NoVideo,
  AddButton,
  SeeMore,
  Actions,
  ActionWrap,
  EmailList,
  EmailListWrap,
} from "../../style/Playlist";
import { FormWrapPopup } from "../../style/DasboardMain";

// Images
import arrow from "../../images/down.svg";
import download from "../../images/ic_download.svg";
import download_blue from "../../images/download_blue.svg";
import share from "../../images/ic_share.svg";
import share_blue from "../../images/share_blue.svg";
import edit from "../../images/ic_edit.svg";
import edit_blue from "../../images/edit_blue.svg";
import trash from "../../images/ic_bin.svg";
import trash_blue from "../../images/trash_blue.svg";
import sort from "../../images/ic_sort.svg";
import play from "../../images/ic_play-button.svg";
import play_blue from "../../images/play_blue.svg";
import add from "../../images/ic_plus.svg";
import add_blue from "../../images/plus_blue.svg";
import dots from "../../images/ic_more.svg";
import closeIcon from "../../images/ic_close.svg";

// Media Query
import { useMediaQuery } from "react-responsive";
import { breakpoints } from "../../style/size";

// Components
import Empty from "../../components/empty/index";
import Modal from "../../components/modal/index";
import TextInput from "../inputs/TextInput";
import Button from "../button";

// Language
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {$apiV1, API_URL_V2, VIDEO_SERVICE_URL} from "../../config/api";
import { useDispatch } from "react-redux";
import { useNotification } from "../notification/useNotification";
import { NotificationList } from "../notification/NotificationList";
import { PlaylistDownloadModal } from "../playlist/PlaylistDownloadModal";
import { usePlaylistDownload } from "../playlist/usePlaylistDownload";
import { useLexicon } from "../../hooks/useLexicon";
import { useSelector } from "react-redux";
import { userActions } from "../../store/user/userSlice";
import { invalidateUserProfile } from "../../helpers/userProfile";
import { formatMatchScore } from "../../helpers/matchScore";
import { LoadingState } from "../../style/Overview";
import { Oval } from "react-loader-spinner";
import { trackPlaylistAction } from "../../tracking";

// Styled components for drag and drop
const DraggableVideoContent = styled(TableContent)`
  cursor: ${props => props.draggable ? 'grab' : 'default'};
  transition: all 0.2s ease;
  opacity: ${props => props.isDragging ? 0.5 : 1};
  transform: ${props => props.isDragging ? 'scale(0.98)' : 'scale(1)'};
  
  &:active {
    cursor: ${props => props.draggable ? 'grabbing' : 'default'};
  }
  
  &.drag-over {
    background-color: ${props => props.theme.blueish3};
    border: 2px dashed ${props => props.theme.green};
  }
  
  &:hover {
    background-color: ${props => props.draggable ? props.theme.blueish3 : 'transparent'};
  }
`;

const DroppablePlaylistContent = styled(TableContent)`
  transition: all 0.2s ease;
  
  &.drag-over {
    background-color: ${props => props.theme.blueish3};
    border: 2px dashed ${props => props.theme.green};
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const DroppableVideoContent = styled(DraggableVideoContent)`
  transition: all 0.2s ease;
  position: relative;
  
  &.drag-over {
    background-color: ${props => props.theme.blueish3};
    border: 2px dashed ${props => props.theme.green};
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &.drag-over-above {
    border-top: 3px solid ${props => props.theme.green};
    background-color: ${props => props.theme.blueish3};
    transform: scale(1.01);
  }
  
  &.drag-over-below {
    border-bottom: 3px solid ${props => props.theme.green};
    background-color: ${props => props.theme.blueish3};
    transform: scale(1.01);
  }
  
  &:hover {
    background-color: ${props => props.draggable ? props.theme.blueish3 : 'transparent'};
  }
`;

const DragIndicator = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: ${props => props.theme.green};
  color: ${props => props.theme.black};
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &.visible {
    opacity: 1;
  }
`;

const DragCopyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 10px;
  cursor: pointer;
`;

const OptionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const OptionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MergeOffsetContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 10px;
`;

const MergeOffsetInput = styled.input`
  width: 60px;
  height: 24px;
  padding: 4px 8px;
  border: 1px solid ${props => props.theme.lines};
  border-radius: 4px;
  background-color: transparent;
  color: ${props => props.theme.white};
  font-size: 12px;
  font-family: "SatoshiRegular";
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.green};
  }
  
  &::placeholder {
    color: ${props => props.theme.blueish};
    opacity: 0.7;
  }
`;

const MergeOffsetLabel = styled.span`
  color: ${props => props.theme.white};
  font-size: 12px;
  font-family: "SatoshiRegular";
  opacity: 0.8;
`;

const StyledCheckbox = styled.input`
  cursor: pointer !important;
  accent-color: ${props => props.theme.green};
  width: 16px;
  height: 16px;
  
  &:checked {
    background-color: ${props => props.theme.green};
    border-color: ${props => props.theme.green};
  }
`;

const CheckboxLabel = styled.label`
  cursor: pointer !important;
  color: ${props => props.theme.white};
  font-size: 14px;
  font-family: "SatoshiMedium";
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const TableWrapContainer = styled.div`
  position: relative;
`;

// Styled Match Breaker Component
const MatchBreakerContent = styled(TableContent)`
  background-color: #738089 !important;
  border-bottom: 1px solid ${(props) => props.theme.lines};
  padding: 8px 20px;
  cursor: default;

  h3 {
    font-size: 14px;
    font-weight: 500;
    font-family: "SatoshiMedium";
    color: ${(props) => props.theme.white};
    margin: 0;
  }

  h5 {
    font-size: 14px;
    color: ${(props) => props.theme.blueish};
    margin: 0;
  }
`;

// Date conversion helper function
const convertIsoToYyyyMmDd = (isoDate) => {
    try {
        const dateObject = new Date(isoDate);
        const year = dateObject.getFullYear();
        const month = dateObject.getMonth() + 1;
        const day = dateObject.getDate();
        const formattedDate = `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
        return formattedDate;
    } catch (error) {
        return '';
    }
};

// Flatten videos with match info helper function
const flattenVideosWithMatchInfo = (videos) => {
    const result = [];
    let currentMatchId = null;
    
    videos.forEach(video => {
        const matchInfo = video?.details?.match || video?.match;
        const matchId = matchInfo?.id || `${matchInfo?.date}_${matchInfo?.home_team?.name}_${matchInfo?.away_team?.name}`;
        
        // Add match breaker if this is a new match
        if (matchInfo && matchId !== currentMatchId) {
            result.push({
                type: 'match',
                match: video,
                matchId: matchId
            });
            currentMatchId = matchId;
        }
        
        // Add the video
        result.push({
            type: 'video',
            video: video,
            matchId: matchId
        });
    });
    
    return result;
};

// Match Breaker Component
const MatchBreaker = ({ match, videos, maxWidthTitle }) => {
    const matchInfo = match?.details?.match || match?.match;
    
    if (!matchInfo) return null;
    
    const matchTitle = ` ${matchInfo.home_squad?.team?.name || matchInfo.home_team?.name || 'Home'} ${formatMatchScore(matchInfo)} ${matchInfo.away_squad?.team?.name || matchInfo.away_team?.name || 'Away'}`;
    
    return (
        <MatchBreakerContent key={`match-${matchInfo.id || matchInfo.date}`}>
            <LeftContent style={{ color: "#fff", cursor: "default" }}>
                <h3 style={{ maxWidth: maxWidthTitle }}>
                    {matchTitle}
                </h3>
            </LeftContent>
            <MidContent>
                <h5>{convertIsoToYyyyMmDd(matchInfo?.date)}</h5>
            </MidContent>
            <RightContent>
                {/* Empty for match breaker */}
            </RightContent>
        </MatchBreakerContent>
    );
};

const MyPlaylist = ({ table, getMyPlaylist, loading = false, onShareSuccess }) => {
  const l = useLexicon();
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: `(${breakpoints.mobile})` });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Sorting State
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState("title");

  // Track open state for each item
  const [openIndex, setOpenIndex] = useState(null);
  const [showActions, setShowActions] = useState(null);
  const [openAddVideo, setOpenAddVide] = useState(false);
  const [openRenameVideo, setOpenRenameVideo] = useState(false);
  const [openDeleteVideo, setOpenDeleteVideo] = useState(false);
  const [openDeleteVideoOnly, setOpenDeleteVideoOnly] = useState(false);
  const [openShareVideo, setOpenShareVideo] = useState(false);
  const [openSuccessShareVideo, setOpenSuccessShareVideo] = useState(false);
  const [openDownloadVideo, setOpenDownloadVideo] = useState(false);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const { notifications, addNotification, removeNotification } = useNotification();
  const playlistDownload = usePlaylistDownload({ addNotification, removeNotification });

  // Drag and Drop State
  const [draggedVideo, setDraggedVideo] = useState(null);
  const [draggedPlaylist, setDraggedPlaylist] = useState(null);
  const [dragOverPlaylist, setDragOverPlaylist] = useState(null);
  const [dragOverVideo, setDragOverVideo] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDragIndicator, setShowDragIndicator] = useState(false);

  const [email, setEmail] = useState("");
  const [folderName, setFolderName] = useState("");
  const [dragCopy, setDragCopy] = useState(false);
  
  // New playlist options state
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [allowMerge, setAllowMerge] = useState(false);
  const [mergeOffset, setMergeOffset] = useState(0);
  
  // Local state for table data to handle reordering
  const [localTable, setLocalTable] = useState(table);
  
  // Note editing state
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  
  // Email autocomplete state
  const [sharedEmails, setSharedEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);

  const handleEmailChange = (newValue) => {
    setFolderName(newValue);
  };

  // Get user profile from Redux store
  const userProfile = useSelector(state => state.user.userProfile);
  const user = useSelector(state => state.user.userProfile?.id);

  // Load drag copy setting from user profile on component mount
  useEffect(() => {
    if (userProfile?.playlist_drag_copy !== undefined) {
      setDragCopy(userProfile.playlist_drag_copy);
    }
    if (userProfile?.playlist_remove_duplicates !== undefined) {
      setRemoveDuplicates(userProfile.playlist_remove_duplicates);
    }
    if (userProfile?.playlist_allow_merge !== undefined) {
      setAllowMerge(userProfile.playlist_allow_merge);
    }
    if (userProfile?.merge_offset !== undefined) {
      setMergeOffset(userProfile.merge_offset);
    }
  }, [userProfile?.playlist_drag_copy, userProfile?.playlist_remove_duplicates, userProfile?.playlist_allow_merge, userProfile?.playlist_merge_offset]);

  // Update local table when prop changes
  useEffect(() => {
    setLocalTable(table);
  }, [table]);

  // Fetch shared emails on component mount
  useEffect(() => {
    fetchSharedEmails();
  }, []);


  const fetchSharedEmails = async () => {
    try {
      const response = await $apiV1.get('/users/shared_emails');
      setSharedEmails(response.data || []);
      setFilteredEmails(response.data || []);
    } catch (error) {
      console.error('Error fetching shared emails:', error);
    }
  };

  // Prevent scrolling when loading overlay is shown
  useEffect(() => {
    if (loading) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position and normal behavior
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [loading]);

  // Save drag copy setting to user profile when it changes
  const saveDragCopySetting = async (newValue) => {
    if (!userProfile) return;
    
    try {
      await $apiV1.patch(`/users/profile_settings/${userProfile.user}/`, {
        playlist_drag_copy: newValue
      });
      // Update Redux store
      dispatch(userActions.setPlaylistDragCopy(newValue));
      invalidateUserProfile();
    } catch (error) {
      console.error("Error saving drag copy setting:", error);
      addNotification("Failed to save drag copy setting", "error");
    }
  };

  // Update drag copy setting
  const handleDragCopyChange = (newValue) => {
    setDragCopy(newValue);
    saveDragCopySetting(newValue);
  };

  // Save remove duplicates setting to user profile
  const saveRemoveDuplicatesSetting = async (newValue) => {
    if (!userProfile) return;
    
    try {
      await $apiV1.patch(`/users/profile_settings/${userProfile.user}/`, {
        playlist_remove_duplicates: newValue
      });
      // Update Redux store
      dispatch(userActions.setPlaylistRemoveDuplicates(newValue));
      invalidateUserProfile();
      getMyPlaylist();
    
    } catch (error) {
      console.error("Error saving remove duplicates setting:", error);
      addNotification("Failed to save remove duplicates setting", "error");
    }
  };

  // Update remove duplicates setting
  const handleRemoveDuplicatesChange = async (newValue) => {
    setRemoveDuplicates(newValue);
    await saveRemoveDuplicatesSetting(newValue);
  };

  // Save allow merge setting to user profile
  const saveAllowMergeSetting = async (newValue) => {
    if (!userProfile) return;
    
    try {
      await $apiV1.patch(`/users/profile_settings/${userProfile.user}/`, {
        playlist_allow_merge: newValue
      });
      // Update Redux store
      dispatch(userActions.setPlaylistAllowMerge(newValue));
      invalidateUserProfile();
    } catch (error) {
      console.error("Error saving allow merge setting:", error);
      addNotification("Failed to save allow merge setting", "error");
    }
  };

  // Update allow merge setting
  const handleAllowMergeChange = (newValue) => {
    setAllowMerge(newValue);
    saveAllowMergeSetting(newValue);

    if (newValue == false) {
      getMyPlaylist();
    }
  };

  // Save merge offset setting to user profile
  const saveMergeOffsetSetting = async (newValue) => {
    if (!userProfile) return;
    
    try {
      await $apiV1.patch(`/users/profile_settings/${userProfile.user}/`, {
        merge_offset: newValue
      });
      // Update Redux store
      dispatch(userActions.changeMergeOffset(newValue));
      invalidateUserProfile();
      getMyPlaylist();
    } catch (error) {
      console.error("Error saving merge offset setting:", error);
      addNotification("Failed to save merge offset setting", "error");
    }
  };

  // Update merge offset setting
  const handleMergeOffsetChange = (newValue) => {
    if (newValue < 1) {
      newValue = 1;
      addNotification(t("playlist.validation.mergeOffsetMin"), "error");
    } else if (newValue > 15) {
      newValue = 15;
      addNotification(t("playlist.validation.mergeOffsetMax"), "error");
    } 
    setMergeOffset(newValue);
    setMergeOffset(newValue);
    saveMergeOffsetSetting(newValue);
  };

  const sortData = (data) => {
    // Ensure data is an array
    if (!Array.isArray(data)) {
      return [];
    }

    // Sorting must not mutate playlist state. The same state is later used as
    // the source for the batch-download request.
    return [...data].sort((a, b) => {
      if (sortBy === "title") {
        if (sortOrder === "asc") {
          return a.title.localeCompare(b.title);
        } else {
          return b.title.localeCompare(a.title);
        }
      } else if (sortBy === "date") {
        if (sortOrder === "asc") {
          return new Date(a.date) - new Date(b.date);
        } else {
          return new Date(b.date) - new Date(a.date);
        }
      }
    });
  };

  const sortedTable = sortData(localTable);

  // Functions for handling sort
  const handleSortByTitle = () => {
    setSortBy("title");
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleSortByDate = () => {
    setSortBy("date");
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  // Function to toggle open state for a specific item
  const toggleOpen = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const toggleShowMore = (index) => {
    setShowActions((prevIndex) => (prevIndex === index ? null : index));
  };

  const addNewFolder = async () => {
    try {
      const response = await $apiV1.post(`/users/playlists/`, { title: folderName });
      trackPlaylistAction({
        action: "create",
        entityId: response?.data?.id,
        entityLabel: response?.data?.title || folderName,
        sourceSurface: "playlist",
      });
      setOpenAddVide(!openAddVideo);
      getMyPlaylist();
    } catch (error) {
      console.error("Error fetching match data:", error);
    }
  };

  const [renameFolderInfo, setRenameFolderInfo] = useState("");

  const handleEdit = (item) => {
    setRenameFolderInfo(item);
    setOpenRenameVideo(!openRenameVideo);
  };

  const handleInputChange = (newValue) => {
    setRenameFolderInfo((prevInfo) => ({
      ...prevInfo,
      title: newValue,
    }));
  };

  const handleRenameFolder = async () => {
    try {
      await $apiV1.put(`/users/playlists/${renameFolderInfo.id}/`, {
        title: renameFolderInfo.title,
      });
      setOpenRenameVideo(!openRenameVideo);
      getMyPlaylist();
    } catch (error) {
      console.error("Error fetching match data:", error);
    }
  };

  const [deleteFolderInfo, setDeleteFolderInfo] = useState("");

  const handleDeleteFolder = (item) => {
    setDeleteFolderInfo(item.id);
    setOpenDeleteVideo(!openDeleteVideo);
  };

  const handleDelete = async (deleteFolderInfo) => {
    try {
      await $apiV1.delete(`/users/playlists/${deleteFolderInfo}/`);
      setOpenDeleteVideo(!openDeleteVideo);
      getMyPlaylist();
    } catch (error) {
      console.error("Error fetching match data:", error);
    }
  };

  const [playlistForVideoDelete, setPlaylistForVideoDelete] = useState(null);
  const [videoForDelete, setVideoForDelete] = useState(null);




  const handleDeleteVideoOnly = async () => {
    try {
        await $apiV1.delete(`/users/playlists/${playlistForVideoDelete}/${videoForDelete}/`);
        trackPlaylistAction({
          action: "video_remove",
          entityId: playlistForVideoDelete,
          entityType: "playlist",
          metadata: {
            playlist_id: playlistForVideoDelete,
            video_id: videoForDelete,
          },
          sourceSurface: "playlist",
        });
        getMyPlaylist();
        setOpenDeleteVideoOnly(!openDeleteVideoOnly);
        setPlaylistForVideoDelete(null);
        setVideoForDelete(null);
    } catch (error) {
        console.error("Error fetching match data:", error);
    }
};

  const [videoName, setVideoName] = useState("");

  const [currentEmail, setCurrentEmail] = useState("");
  const [emailList, setEmailList] = useState([]);

  const handleShareVideo = (item) => {
    // Check if playlist is blocked
    if (item?.is_blocked === true) {
      setShowAccessDeniedModal(true);
      return;
    }
    setVideoName(item);
    setOpenShareVideo(!openShareVideo);
    setEmailList([]);
    setCurrentEmail("");
  };

  const handleEmailList = (newValue) => {
    setCurrentEmail(newValue);
    
    if (newValue.trim()) {
      const filtered = sharedEmails.filter(email => 
        typeof email === 'string' && email.toLowerCase().includes(newValue.toLowerCase())
      );

      setFilteredEmails(filtered);
      setShowEmailDropdown(filtered.length > 0);
    } else {
      setFilteredEmails(sharedEmails);
      setShowEmailDropdown(true);
    }
  };

  const handleAddEmail = () => {
    setEmailList([...emailList, currentEmail]);
    setCurrentEmail("");
  };

  const handleRemoveEmail = (index) => {
    setEmailList(emailList.filter((_, i) => i !== index));
  };

  useEffect(() => { }, [emailList]);

  const handleSharePlayers = async (videoName) => {
    try {
      await $apiV1.post(`/users/share/`, {
        email: emailList,
        playlist: videoName.id,
      });
      trackPlaylistAction({
        action: "share",
        entityId: videoName?.id,
        entityLabel: videoName?.title || null,
        metadata: {
          recipients_count: emailList.length,
          shared_with: emailList,
        },
        sourceSurface: "playlist",
      });
      setOpenSuccessShareVideo(true);
      setOpenShareVideo(false);
      await fetchSharedEmails();
      if (typeof onShareSuccess === "function") {
        await onShareSuccess();
      }
    } catch (error) {
      console.error("Error fetching match data:", error);
    }
  };

  // Helper function to determine drop position
  const getDropPosition = (e, targetElement) => {
    const rect = targetElement.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    return y < height / 2 ? 'above' : 'below';
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, video, playlist) => {
    // Don't allow dragging if the playlist is collapsed
    const playlistIndex = sortedTable.findIndex(item => item.id === playlist.id);
    if (openIndex !== playlistIndex) {
      e.preventDefault();
      return;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ video, playlist }));
    setDraggedVideo(video);
    setDraggedPlaylist(playlist);
    setIsDragging(true);
    setShowDragIndicator(true);
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    setShowDragIndicator(false);
    setDraggedVideo(null);
    setDraggedPlaylist(null);
    setDragOverPlaylist(null);
    setDragOverVideo(null);
  };

  const handleDragOver = (e, target, type = 'playlist') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (type === 'playlist') {
      // Only allow dropping if the playlist is different from the source
      if (draggedPlaylist && draggedPlaylist.id !== target.id) {
        setDragOverPlaylist(target);
        setDragOverVideo(null);
      }
    } else if (type === 'video') {
      // Handle video drop for reordering within same playlist
      if (draggedPlaylist && draggedPlaylist.id === target.playlist.id) {
        const dropPosition = getDropPosition(e, e.currentTarget);
        setDragOverVideo({ ...target, dropPosition });
        setDragOverPlaylist(null);
      }
    }
  };

  const handleDragLeave = (e, type = 'playlist') => {
    // Only clear if we're actually leaving the element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (type === 'playlist') {
        setDragOverPlaylist(null);
      } else if (type === 'video') {
        setDragOverVideo(null);
      }
    }
  };

  const handleDrop = async (e, target, type = 'playlist') => {
    e.preventDefault();
    
    if (!draggedVideo || !draggedPlaylist) {
      setDragOverPlaylist(null);
      setDragOverVideo(null);
      return;
    }

    if (type === 'playlist') {
      // Handle dropping on playlist (move between playlists)
      if (draggedPlaylist.id === target.id) {
        setDragOverPlaylist(null);
        return;
      }

      try {
        // Move video to target playlist
        await $apiV1.post(`/users/playlists/${target.id}/add`, [{
          event_id: draggedVideo.details.event_id,
          title: draggedVideo.title
        }]);
        trackPlaylistAction({
          action: "video_add",
          entityId: target?.id,
          entityLabel: target?.title || null,
          metadata: {
            dragged_video_id: draggedVideo?.id,
            dragged_video_title: draggedVideo?.title || null,
            drag_mode: dragCopy ? "copy" : "move",
            source_playlist_id: draggedPlaylist?.id,
            target_playlist_id: target?.id,
          },
          sourceSurface: "playlist",
        });
        
        if (!dragCopy) {  
          // Remove video from source playlist
          await $apiV1.delete(`/users/playlists/${draggedPlaylist.id}/${draggedVideo.id}/`);
          trackPlaylistAction({
            action: "video_remove",
            entityId: draggedPlaylist?.id,
            entityLabel: draggedPlaylist?.title || null,
            metadata: {
              drag_mode: "move",
              source_playlist_id: draggedPlaylist?.id,
              target_playlist_id: target?.id,
              video_id: draggedVideo?.id,
            },
            sourceSurface: "playlist",
          });
        }

        // Update local state
        const updatedTable = localTable.map(playlist => {
          if (playlist.id === target.id) {
            // Add video to target playlist
            return {
              ...playlist,
              videos: [...playlist.videos, draggedVideo]
            };
          } else if (!dragCopy && playlist.id === draggedPlaylist.id) {
            // Remove video from source playlist (only if not copying)
            return {
              ...playlist,
              videos: playlist.videos.filter(video => video.id !== draggedVideo.id)
            };
          }
          return playlist;
        });
        
        // Update the local table state
        setLocalTable(updatedTable);

        if (dragCopy) {
          addNotification(`Video "${draggedVideo.title}" copied to "${target.title}"`, 'success');
        } else {
          addNotification(`Video "${draggedVideo.title}" moved to "${target.title}"`, 'success');
        }
      } catch (error) {
        console.error("Error moving video:", error);
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
        addNotification(`Failed to move video: ${errorMessage}`, 'error');
      }

      setDragOverPlaylist(null);
    } else if (type === 'video') {
      // Handle dropping on video (reorder within same playlist)
      if (draggedPlaylist.id !== target.playlist.id) {
        setDragOverVideo(null);
        return;
      }

      try {
        // Get current video order
        const currentVideos = [...target.playlist.videos];
        const draggedIndex = currentVideos.findIndex(v => v.id === draggedVideo.id);
        const targetIndex = currentVideos.findIndex(v => v.id === target.video.id);
        
        if (draggedIndex === -1 || targetIndex === -1) {
          throw new Error('Video not found in playlist');
        }

        // Remove dragged video from its current position
        const [removedVideo] = currentVideos.splice(draggedIndex, 1);
        
        // Insert at target position (adjust for the removal)
        let newIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
        
        // If dropping below, move the index down by 1
        if (target.dropPosition === 'below') {
          newIndex += 1;
        }
        
        currentVideos.splice(newIndex, 0, removedVideo);

        // Create array of video IDs in new order
        const videoOrder = currentVideos.map(video => video.id);

                // Call API to reorder videos
        await $apiV1.put(`/users/playlists/reorder/${target.playlist.id}`, {
          video_order: videoOrder
        });

        // reorder locally
        const updatedTable = localTable.map(playlist => {
          if (playlist.id === target.playlist.id) {
            return {
              ...playlist,
              videos: currentVideos
            };
          }
          return playlist;
        });
        
        // Update the local table state with the new order
        setLocalTable(updatedTable);
    
        addNotification(`Video "${draggedVideo.title}" reordered in "${target.playlist.title}"`, 'success');
      } catch (error) {
        console.error("Error reordering video:", error);
        const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
        addNotification(`Failed to reorder video: ${errorMessage}`, 'error');
      }

      setDragOverVideo(null);
    }
  };

  const handleDownloadSelectedCuts = (data, playlistItem = null) => {
    // Check if playlist is blocked
    if (playlistItem?.is_blocked === true) {
      setShowAccessDeniedModal(true);
      return;
    }

    playlistDownload.startDownload({
      videos: Array.isArray(data) ? data : [],
      mode: "video",
      title: playlistItem?.title || "",
      tracking: {
        entityId: playlistItem?.id || null,
        entityLabel: playlistItem?.title || null,
        entityType: playlistItem?.id ? "playlist" : "video",
        sourceSurface: "playlist",
        downloadMode: "combined_clip",
      },
    });
  };

  const handleNoteClick = (video) => {
    setEditingNote(video.id);
    setNoteText(video.note || "");
  };

  const handleNoteSubmit = async (id, note) => {
    try {
      await $apiV1.put('/users/playlist/update-note', {id, note});
      
      // Update local state
      const updatedTable = localTable.map(playlist => ({
        ...playlist,
        videos: playlist.videos.map(v => 
          v.id === id ? { ...v, note: note } : v
        )
      }));
      setLocalTable(updatedTable);
      
      addNotification('Note updated successfully', 'success');
    } catch (error) {
      console.error("Error updating note:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      addNotification(`Failed to update note: ${errorMessage}`, 'error');
    } finally {
      setEditingNote(null);
      setNoteText("");
    }
  };

  const handleNoteCancel = () => {
    setEditingNote(null);
    setNoteText("");
  };

  const handleEmailSelect = (email) => {
    setCurrentEmail(email);
    setShowEmailDropdown(false);
    setFilteredEmails([]);
  };

  const modal = (
    <>
      {/* Add Folder */}
      <Modal isOpen={openAddVideo} setIsOpen={setOpenAddVide} closeIc>
        <h1>{l(975)}</h1>
        {/* <p>{t("playlist.addFolderDesc")}</p> */}
        <FormWrapPopup style={{ marginTop: "20px" }}>
          <TextInput
            label={l(970)}
            type={"text"}
            value={folderName}
            placeholder={l(970)}
            onChange={handleEmailChange}
            required
          />
        </FormWrapPopup>
        <Button
          title={l(976)}
          size={"full"}
          // onClick={() => setOpenAddVide(!openAddVideo)}
          onClick={() => addNewFolder()}
        />
      </Modal>

      {/* Rename Folder */}
      <Modal isOpen={openRenameVideo} setIsOpen={setOpenRenameVideo} closeIc>
        <h1>{l(969)}</h1>
        {/* <p>{t("playlist.renameFolderDesc")}</p> */}
        <FormWrapPopup style={{ marginTop: "20px" }}>
          <TextInput
            label={l(970)}
            type={"text"}
            value={renameFolderInfo.title}
            placeholder={l(970)}
            onChange={handleInputChange}
            required
          />
        </FormWrapPopup>
        <Button
          title={l(971)}
          size={"full"}
          onClick={() => handleRenameFolder()}
        />
      </Modal>

      {/* Delete Folder */}
      <Modal isOpen={openDeleteVideo} setIsOpen={setOpenDeleteVideo} closeIc>
        <h1>{l(972)}</h1>
        <p style={{ marginBottom: "20px" }}>{l(973)}</p>
        <Button
          title={l(974)}
          size={"full"}
          onClick={() => handleDelete(deleteFolderInfo)}
        />
      </Modal>

      {/* Delete Video */}
      <Modal isOpen={openDeleteVideoOnly} setIsOpen={setOpenDeleteVideoOnly} closeIc>
        <h1>{t("playlist.deleteVideoTitle")}</h1>
        <p style={{ marginBottom: "20px" }}>{t("playlist.deleteVideoDescription")}</p>
        <Button
          title={t("playlist.deleteFolderButton")}
          size={"full"}
          onClick={() => handleDeleteVideoOnly()}
        />
      </Modal>
      

      {/* Share Video */}
      <Modal isOpen={openShareVideo} setIsOpen={setOpenShareVideo} closeIc>
        <h1>{l(965)}</h1>
        <p>
          Share {videoName?.title} via next social media, or just copy link.
        </p>
        <div style={{ position: 'relative' }}>
          <TextInput
            type="email"
            value={currentEmail}
            onChange={handleEmailList}
            placeholder={l(966)}
            onFocus={() => {
              setShowEmailDropdown(true);
            }}
            onBlur={() => {
              // Delay hiding dropdown to allow for clicks
              setTimeout(() => setShowEmailDropdown(false), 200);
            }}
          />
          {showEmailDropdown && filteredEmails.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'black',
              color: 'white',
              textAlign: 'left',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {filteredEmails.slice(0, 3).map((email, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: index < filteredEmails.length - 1 ? '1px solid #eee' : 'none',
                    fontSize: '14px',
                    fontFamily: 'SatoshiRegular'
                  }}
                  onMouseDown={() => handleEmailSelect(email)}
                >
                  {email}
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          title={l(967)}
          size={"full"}
          color={"secondary"}
          onClick={() => handleAddEmail(currentEmail)}
          style={{ marginTop: "30px" }}
        />

        {emailList.length > 0 && (
          <EmailList>
            {emailList.map((email, index) => (
              <EmailListWrap key={index}>
                {email}{" "}
                <img
                  src={closeIcon}
                  alt={closeIcon}
                  onClick={() => handleRemoveEmail(index)}
                />
              </EmailListWrap>
            ))}
            <Button
              title={l(968)}
              size={"full"}
              color={"secondary"}
              onClick={() => handleSharePlayers(videoName)}
              style={{ marginTop: "30px" }}
            />
          </EmailList>
        )}
      </Modal>

      {/* Success Share Video */}
      <Modal
        isOpen={openSuccessShareVideo}
        setIsOpen={setOpenSuccessShareVideo}
        successIc
      >
        <h1>{l(439)}</h1>
        <p style={{ marginBottom: "40px" }}>{l(1055)}</p>

        <Button
          title={l(1056)}
          size={"full"}
          onClick={() => setOpenSuccessShareVideo(!openSuccessShareVideo)}
        />
      </Modal>
      {/* Access Denied Modal */}
      <Modal isOpen={showAccessDeniedModal} setIsOpen={setShowAccessDeniedModal} closeIc>
        <h1>Access Denied</h1>
        <p>Access denied. Please contact your account manager or remove videos you do not have permission to access.</p>
        <br></br>
        <Button
          title="Close"
          size={"full"}
          onClick={() => setShowAccessDeniedModal(false)}
        />
      </Modal>
    </>
  );

  return (
    <>
             <OptionsContainer>
         <OptionGroup>
           <StyledCheckbox 
             type="checkbox"
             name="dragCopy"
             checked={dragCopy}
             onChange={() => handleDragCopyChange(!dragCopy)}
           />
           <CheckboxLabel htmlFor="dragCopy" onClick={() => handleDragCopyChange(!dragCopy)}>{l(1110)}</CheckboxLabel>
         </OptionGroup>
         
         <OptionGroup>
           <StyledCheckbox 
             type="checkbox"
             name="removeDuplicates"
             checked={removeDuplicates}
             onChange={() => handleRemoveDuplicatesChange(!removeDuplicates)}
           />
           <CheckboxLabel htmlFor="removeDuplicates" onClick={() => handleRemoveDuplicatesChange(!removeDuplicates)}>Remove duplicates</CheckboxLabel>
         </OptionGroup>
         
         <OptionGroup>
           <StyledCheckbox 
             type="checkbox"
             name="allowMerge"
             checked={allowMerge}
             onChange={() => handleAllowMergeChange(!allowMerge)}
           />
           <CheckboxLabel htmlFor="allowMerge" onClick={() => handleAllowMergeChange(!allowMerge)}>Allow merge</CheckboxLabel>
           
           {allowMerge && (
             <MergeOffsetContainer>
               <MergeOffsetLabel>Offset:</MergeOffsetLabel>
               <MergeOffsetInput
                 type="number"
                 min={1}
                 max={15}
                 value={mergeOffset}
                 onChange={(e) => handleMergeOffsetChange(e.target.value)}
                 placeholder="0"
               />
             </MergeOffsetContainer>
           )}
         </OptionGroup>
       </OptionsContainer>
      <DragIndicator className={showDragIndicator ? 'visible' : ''}>
        {draggedVideo ? (
          dragOverVideo ? 
            `Reordering: ${draggedVideo.title}` : 
            `Moving: ${draggedVideo.title}`
        ) : 'Moving video...'}
      </DragIndicator>
      <TableNames>
        <AlfabetSort onClick={handleSortByTitle} style={{ cursor: "pointer" }}>
          <h6>{l(962)}</h6>
          <img src={sort} alt="sort" />
        </AlfabetSort>
        <DateSort onClick={handleSortByDate} style={{ cursor: "pointer" }}>
          <h6>{l(132)}</h6>
          <img src={sort} alt="sort" />
        </DateSort>
      </TableNames>
      <div style={{ position: 'relative' }}>
        {sortedTable.length > 0 ? (
          <TableWrapContainer>
            <TableWrap>
            {sortedTable.map((table_item, index) => {
              return (
                <>
                  <DroppablePlaylistContent 
                    key={index}
                    className={dragOverPlaylist?.id === table_item.id ? 'drag-over' : ''}
                    onDragOver={(e) => handleDragOver(e, table_item)}
                    onDragLeave={(e) => handleDragLeave(e, 'playlist')}
                    onDrop={(e) => handleDrop(e, table_item)}
                    title={isDragging && draggedPlaylist?.id !== table_item.id ? `Drop video here to move to "${table_item.title}"` : ''}
                  >
                    <LeftContent
                      style={{ color: "#fff" }}
                      onClick={() => toggleOpen(index)}
                      onContextMenu={e => {
                        e.preventDefault()
                        window.open(`/my-playlists/${table_item.id}`)
                      }}
                    >
                      <img src={arrow} alt="arrow" /> <h3>{table_item.title}</h3>
                    </LeftContent>
                    <MidContent>
                      <h5>{table_item.date?.split("T")?.[0]}</h5>
                    </MidContent>
                    <RightContent>
                      {!isMobile ? (
                        <>
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? download_blue
                                : download
                            }
                            alt="download"
                            style={{
                              pointerEvents: playlistDownload.isBusy ? 'none' : 'auto',
                              opacity: playlistDownload.isBusy ? 0.5 : 1,
                            }}
                            onClick={() =>
                              handleDownloadSelectedCuts(table_item?.videos, table_item)
                            }
                          />
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? share_blue
                                : share
                            }
                            alt="share"
                            onClick={() => handleShareVideo(table_item)}
                          />
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? edit_blue
                                : edit
                            }
                            alt="edit"
                            onClick={() => handleEdit(table_item)}
                          />
                          <img
                            src={
                              localStorage.getItem("theme") === "light"
                                ? trash_blue
                                : trash
                            }
                            alt="trash"
                            onClick={() => handleDeleteFolder(table_item)}
                          />
                        </>
                      ) : (
                        <SeeMore>
                          <img
                            src={dots}
                            alt="dots"
                            onClick={() => toggleShowMore(index)}
                          />
                          {showActions === index && (
                            <Actions>
                              <ActionWrap
                                style={{
                                  pointerEvents: playlistDownload.isBusy ? 'none' : 'auto',
                                  opacity: playlistDownload.isBusy ? 0.5 : 1,
                                }}
                                onClick={() =>
                                  handleDownloadSelectedCuts(table_item?.videos, table_item)
                                }
                              >
                                <img
                                  src={
                                    localStorage.getItem("theme") === "light"
                                      ? download_blue
                                      : download
                                  }
                                  alt="download"
                                />
                                <h6>{l(116)}</h6>
                              </ActionWrap>
                              <ActionWrap
                                onClick={() => handleShareVideo(table_item)}
                              >
                                <img
                                  src={
                                    localStorage.getItem("theme") === "light"
                                      ? share_blue
                                      : share
                                  }
                                  alt="share"
                                />
                                <h6>{l(968)}</h6>
                              </ActionWrap>
                              <ActionWrap
                                onClick={() =>
                                  setOpenRenameVideo(!openRenameVideo)
                                }
                              >
                                <img
                                  src={
                                    localStorage.getItem("theme") === "light"
                                      ? edit_blue
                                      : edit
                                  }
                                  alt="edit"
                                />
                                <h6>{l(971)}</h6>
                              </ActionWrap>
                              <ActionWrap
                                onClick={() =>
                                  setOpenDeleteVideo(!openDeleteVideo)
                                }
                              >
                                <img
                                  src={
                                    localStorage.getItem("theme") === "light"
                                      ? trash_blue
                                      : trash
                                  }
                                  alt="trash"
                                />
                                <h6>{l(974)}</h6>
                              </ActionWrap>
                            </Actions>
                          )}
                        </SeeMore>
                      )}
                    </RightContent>
                  </DroppablePlaylistContent>

                  {openIndex === index && (
                    <>
                      {table_item.videos.length > 0 ? (
                        flattenVideosWithMatchInfo(table_item.videos).map((item, itemIndex) => {
                          if (item.type === 'match') {
                            return (
                              <MatchBreaker
                                key={`match-${item.matchId}`}
                                match={item.match}
                                videos={[]}
                              />
                            );
                          } else {
                            const video = item.video;
                            const isDragOver = dragOverVideo?.video?.id === video.id;
                            const dropPosition = dragOverVideo?.dropPosition;
                            const dragOverClass = isDragOver ? 
                              (dropPosition === 'above' ? 'drag-over-above' : 
                               dropPosition === 'below' ? 'drag-over-below' : 'drag-over') : '';
                            
                            return (
                              <DroppableVideoContent 
                                key={`video-${video.id}`}
                                draggable={openIndex === index}
                                isDragging={isDragging && draggedVideo?.id === video.id}
                                className={dragOverClass}
                                onDragStart={(e) => handleDragStart(e, video, table_item)}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, { video: video, playlist: table_item }, 'video')}
                                onDragLeave={(e) => handleDragLeave(e, 'video')}
                                onDrop={(e) => handleDrop(e, { video: video, playlist: table_item }, 'video')}
                                title={openIndex === index ? "Drag to move this video to another playlist or drop on another video to reorder" : "Expand playlist to drag videos"}
                              >
                                <LeftContent
                                  style={{ color: "#fff" }}
                                  onClick={() =>
                                    navigate(
                                      `/my-playlists/${table_item.id}/?video=${video.id}`
                                    )
                                  }
                                  onContextMenu={(e) => {
                                    e.preventDefault()
                                    window.open(
                                      `/my-playlists/${table_item.id}/?video=${video.id}`
                                    )
                                  }
                                  }
                                >
                                  <img
                                    src={
                                      localStorage.getItem("theme") === "light"
                                        ? play_blue
                                        : play
                                    }
                                    alt="play"
                                  />{" "}
                                  <h3>{video.title}</h3>
                                </LeftContent>
                                <MidContent>
                                  {editingNote === video.id ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <input
                                        type="text"
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        style={{
                                          flex: 1,
                                          padding: '4px 8px',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          fontSize: '16px',
                                          fontFamily: 'SatoshiRegular',
                                          color: 'white',
                                          background: 'transparent',
                                        }}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            handleNoteSubmit(video.id, e.target.value);
                                          } else if (e.key === 'Escape') {
                                            handleNoteCancel();
                                          }
                                        }}
                                        autoFocus
                                      />
                            
                                    </div>
                                  ) : (
                                    <h5 
                                      style={{ 
                                        textOverflow: 'ellipsis', 
                                        overflow: 'hidden', 
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px'
                                      }}
                                      onClick={() => handleNoteClick(video)}
                                
                                    >
                                      {video.note ? video.note : <span style={{ opacity: 0.4, color: 'white', fontFamily: 'SatoshiRegular' }}>{l(1111)}</span>}
                                    </h5>
                                  )}
                                </MidContent>
                                <RightContent>
                                  {!isMobile ? (
                                    <>
                                      <img
                                        src={
                                          localStorage.getItem("theme") === "light"
                                            ? download_blue
                                            : download
                                        }
                                        alt="download"
                                        style={{
                                          pointerEvents: playlistDownload.isBusy ? 'none' : 'auto',
                                          opacity: playlistDownload.isBusy ? 0.5 : 1,
                                        }}
                                        onClick={() =>
                                          handleDownloadSelectedCuts([video], table_item)
                                        }
                                      />
                                      <img
                                        src={
                                          localStorage.getItem("theme") === "light"
                                            ? share_blue
                                            : share
                                        }
                                        alt="share"
                                        onClick={() => {
                                          if (table_item?.is_blocked === true) {
                                            setShowAccessDeniedModal(true);
                                            return;
                                          }
                                          setVideoName(table_item);
                                          setOpenShareVideo(!openShareVideo);
                                          setEmailList([]);
                                          setCurrentEmail("");
                                        }}
                                      />
                                      <img
                                        src={
                                          localStorage.getItem("theme") === "light"
                                            ? edit_blue
                                            : edit
                                        }
                                        alt="edit"
                                        onClick={() =>
                                          setOpenRenameVideo(!openRenameVideo)
                                        }
                                      />
                                      <img
                                        src={
                                          localStorage.getItem("theme") === "light"
                                            ? trash_blue
                                            : trash
                                        }
                                        alt="trash"
                                        onClick={() =>
                                          {
                                            setPlaylistForVideoDelete(table_item.id)
                                            setVideoForDelete(video.id)
                                            setOpenDeleteVideoOnly(!openDeleteVideoOnly)
                                          }
                                        }
                                      />
                                    </>
                                  ) : (
                                    <SeeMore>
                                      <img
                                        src={dots}
                                        alt="dots"
                                        onClick={() => toggleShowMore(index)}
                                      />
                                      {showActions === index && (
                                        <Actions>
                                          <ActionWrap
                                            style={{
                                              pointerEvents: playlistDownload.isBusy ? 'none' : 'auto',
                                              opacity: playlistDownload.isBusy ? 0.5 : 1,
                                            }}
                                            onClick={() =>
                                              handleDownloadSelectedCuts(table_item?.videos, table_item)
                                            }
                                          >
                                            <img
                                              src={
                                                localStorage.getItem("theme") ===
                                                  "light"
                                                  ? download_blue
                                                  : download
                                              }
                                              alt="download"
                                            />
                                            <h6>{l(116)}</h6>
                                          </ActionWrap>
                                          <ActionWrap
                                            onClick={() =>
                                              setOpenShareVideo(!openShareVideo)
                                            }
                                          >
                                            <img
                                              src={
                                                localStorage.getItem("theme") ===
                                                  "light"
                                                  ? share_blue
                                                  : share
                                              }
                                              alt="share"
                                            />
                                            <h6>{l(968)}</h6>
                                          </ActionWrap>
                                          <ActionWrap
                                            onClick={() =>
                                              setOpenRenameVideo(!openRenameVideo)
                                            }
                                          >
                                            <img
                                              src={
                                                localStorage.getItem("theme") ===
                                                  "light"
                                                  ? edit_blue
                                                  : edit
                                              }
                                              alt="edit"
                                            />
                                            <h6>{l(971)}</h6>
                                          </ActionWrap>
                                          <ActionWrap
                                             onClick={() =>
                                              {
                                                setPlaylistForVideoDelete(table_item.id)
                                                setVideoForDelete(video.id)
                                                setOpenDeleteVideoOnly(!openDeleteVideoOnly)
                                              }
                                            }
                                          >
                                            <img
                                              src={
                                                localStorage.getItem("theme") ===
                                                  "light"
                                                  ? trash_blue
                                                  : trash
                                              }
                                              alt="trash"
                                            />
                                            <h6>{l(974)}</h6>
                                          </ActionWrap>
                                        </Actions>
                                      )}
                                    </SeeMore>
                                  )}
                                </RightContent>
                              </DroppableVideoContent>
                            );
                          }
                        })
                      ) : (
                        <NoVideo>
                          <h6>{l(1057)}</h6>
                        </NoVideo>
                      )}
                    </>
                  )}
                </>
              );
            })}

            <AddButton onClick={() => setOpenAddVide(!openAddVideo)}>
              <img
                src={localStorage.getItem("theme") === "light" ? add_blue : add}
                alt="add"
              />
            </AddButton>
          </TableWrap>
          </TableWrapContainer>
        ) : (
          <Empty
            title={t("playlist.emptyPlaylistTitle")}
            text={t("playlist.emptyPlaylistDesc")}
            button1={t("playlist.emptyPlaylistButton")}
          />
        )}
        
        {loading && (
          <LoadingOverlay>
            <Oval
              visible={true}
              height="100"
              width="100"
              secondaryColor={
                localStorage.getItem("theme") === "light"
                  ? "#4E5255"
                  : "#CFDAE6"
              }
              color={
                localStorage.getItem("theme") === "light"
                  ? "#2363F7"
                  : "#4fa94d"
              }
              ariaLabel="oval-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </LoadingOverlay>
        )}
      </div>
      {modal}
      <PlaylistDownloadModal
        download={playlistDownload.partsDownload}
        onStop={playlistDownload.stopDownload}
        onResume={playlistDownload.resumeDownload}
        onClose={playlistDownload.closeDownload}
        onPartDownloaded={playlistDownload.markPartDownloaded}
      />

      <NotificationList notifications={notifications} onClose={removeNotification} />
    </>
  );
};

export default MyPlaylist;
